import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { Component } from 'react'

import Spinner from '../Spinner/Spinner'

import style from './Table.scss'
import TableCell from './TableCell'
import { TablePager } from './TablePager'

class Table extends Component {
  constructor(props) {
    super(props)
    this.state = {
      collapsedColumns: [],
      page: 1,
      isLoading: true,
      data: [],
      total: 0,
      isUpdating: false,
      sorting: props.sorting ? props.sorting : {},
    }

    this.collapseColumn.bind(this)
    this.expandColumn.bind(this)
    this.cellRef = React.createRef()
  }

  componentDidMount() {
    this.load(1, this.props.sorting)
  }

  load(page, sorting) {
    this.setState({ isLoading: true })

    this.props.dataProvider
      .rows((page - 1) * this.props.pageSize, this.props.pageSize, sorting)
      .then(result => {
        this.setState({
          data: result.data.slice(0),
          total: result.count,
          isLoading: false,
          page,
        })
      })
  }

  expandColumn(columnIndex) {
    const index = this.state.collapsedColumns.indexOf(columnIndex)
    if (-1 === index) return

    const columns = this.state.collapsedColumns.slice(0)
    columns.splice(index, 1)

    this.setState({ collapsedColumns: columns })
  }

  collapseColumn(columnIndex) {
    this.setState(prevState => ({
      collapsedColumns: [...prevState.collapsedColumns, columnIndex],
    }))
  }

  sortColumn(columnIndex, isAscending) {
    const column = this.props.children[columnIndex]
    const sorting = {
      ...this.state.sorting,
      [column.props.dataKey]: isAscending ? 'ASC' : 'DESC',
    }
    if (null === isAscending) delete sorting[column.props.dataKey]

    this.setState({ sorting })
    this.load(this.state.page, sorting)
  }

  isCollapsed(columnIndex) {
    return this.state.collapsedColumns.includes(columnIndex)
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.version !== this.props.version) {
      this.load(1, this.state.sorting)
    }
  }

  onUpdate(column, value, row) {
    if (typeof this.props.dataProvider.update !== 'function') {
      return
    }

    const data = this.state.data.slice(0)
    const dataIndex = data.findIndex(oneData => oneData.id === row.id)
    if (column.props.reverseTransform)
      value = column.props.reverseTransform(value)
    if (value === data[dataIndex][column.props.dataKey]) return

    data[dataIndex][column.props.dataKey] = value

    this.setState({ isUpdating: true, data })

    if (!this.isValid(data[dataIndex])) {
      this.setState({ isUpdating: false })
      return
    }

    this.props.dataProvider
      .update(row.id, column.props.dataKey, value)
      .then(() => this.setState({ isUpdating: false }))
  }

  isValid(row) {
    let columns = this.props.children
    if (!Array.isArray(columns)) columns = [columns]

    for (let i = 0; i < columns.length; i++) {
      const column = columns[i]
      if (
        column.props.validate &&
        !column.props.validate(row[column.props.dataKey])
      )
        return false
    }

    return true
  }

  render() {
    let { dataProvider, children, pageSize } = this.props
    const { page, data, isLoading, isUpdating, sorting, collapsedColumns } =
      this.state
    let hasEditableColumn = false

    if (!Array.isArray(children)) children = [children]

    const columns = children.map((column, columnIndex) => {
      hasEditableColumn = hasEditableColumn || column.props.editable
      let sort = null
      if (sorting[column.props.dataKey] === 'ASC') {
        sort = true
      } else if (sorting[column.props.dataKey] === 'DESC') {
        sort = false
      }

      return React.cloneElement(column, {
        onCollapse: columnIndex => this.collapseColumn(columnIndex),
        onExpand: columnIndex => this.expandColumn(columnIndex),
        onSort: (columIndex, isAscending) =>
          this.sortColumn(columnIndex, isAscending),
        index: columnIndex,
        key: 'header' + columnIndex,
        sort,
      })
    })

    const headerRow = <tr>{columns}</tr>

    const rows = data.map((row, rowIndex) => {
      const cells = []

      for (let i = 0; i < columns.length; i++) {
        if (rowIndex > 0 && this.isCollapsed(i)) continue
        const column = columns[i]
        const classNames = []
        if (this.isCollapsed(i)) classNames.push(style.collapsed)
        if (
          column.props.validate &&
          !column.props.validate(row[column.props.dataKey])
        )
          classNames.push(style.error)

        cells.push(
          <TableCell
            value={
              this.isCollapsed(i) ? (
                <div>
                  <div>{column.props.children}</div>
                </div>
              ) : (
                row[column.props.dataKey]
              )
            }
            row={row}
            classNames={classNames}
            rowspan={this.isCollapsed(i) ? dataProvider.count() : 1}
            editable={column.props.editable}
            onBlur={value => this.onUpdate(column, value, row)}
            choices={column.props.choices}
            colors={column.props.colors}
            key={'tableCell_' + rowIndex + '_' + i}
            transform={column.props.transform}
            cellStyle={column.props.cellStyle}
            iconMap={column.props.iconMap}
            url={column.props.url}
            collapsed={this.isCollapsed(i)}
            isCheckbox={!!column.props.isCheckbox}
          />,
        )
      }

      if (cells.length === 0) return ''

      return (
        <tr
          className={rowIndex % 2 === 0 ? style.odd : style.even}
          key={'row' + rowIndex}>
          {cells}
        </tr>
      )
    })

    return (
      <div>
        <div className={style.tableWrapper}>
          {isLoading ? (
            <div className={style.loaderWrapper}>
              <Spinner />
            </div>
          ) : (
            ''
          )}
          {hasEditableColumn ? (
            <div className={style.tableStatus}>
              {isUpdating ? (
                <Spinner isInline />
              ) : (
                <FontAwesomeIcon
                  icon={faCheck}
                  className={style.tableStatusSuccess}
                />
              )}
            </div>
          ) : (
            ''
          )}
          <table className={style.table}>
            <thead>{headerRow}</thead>
            <tbody>{rows}</tbody>
          </table>
        </div>

        <TablePager
          pages={Math.ceil(dataProvider.count() / pageSize)}
          currentPage={page}
          onPageClick={page => this.load(page, sorting)}
        />
      </div>
    )
  }
}

export default Table
